import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  Trophy, 
  DollarSign, 
  Target,
  Zap,
  TrendingUp,
  Award,
  Share2,
  Upload,
  CheckCircle,
  Building,
  Mail,
  Phone,
  Globe,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';

const Partnership = () => {
  const [formData, setFormData] = useState({
    brandName: '',
    contactPerson: '',
    email: '',
    phone: '',
    website: '',
    partnershipType: '',
    productCategory: '',
    message: ''
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const whyPartnerReasons = [
    {
      icon: Users,
      title: "Active Young Gamers",
      description: "Access young and active quiz gamers across India"
    },
    {
      icon: Trophy,
      title: "Hero Reward Status",
      description: "Your product becomes the hero reward in our games"
    },
    {
      icon: Share2,
      title: "Organic Sharing",
      description: "Instant organic sharing from winners"
    },
    {
      icon: Target,
      title: "High Engagement",
      description: "High consumer engagement & brand recall"
    },
    {
      icon: TrendingUp,
      title: "Performance-Driven",
      description: "Performance-driven and transparent campaigns"
    }
  ];

  const partnershipOpportunities = [
    {
      icon: Trophy,
      title: "Prize Sponsorship",
      description: "Your products as exciting prizes that players compete for"
    },
    {
      icon: Zap,
      title: "Branded Quiz Rounds",
      description: "Custom quiz rounds featuring your brand and products"
    },
    {
      icon: Target,
      title: "Product Launch Promotions",
      description: "Launch new products through engaging gaming campaigns"
    },
    {
      icon: Award,
      title: "Featured Brand Campaigns",
      description: "Exclusive campaigns highlighting your brand story"
    },
    {
      icon: DollarSign,
      title: "Affiliate & Performance",
      description: "Performance-based partnerships with measurable ROI"
    }
  ];

  const stats = [
    { label: "Total Players", value: "50,000+", icon: Users },
    { label: "Games Played", value: "2.5M+", icon: Trophy },
    { label: "Rewards Distributed", value: "₹1 Cr+", icon: DollarSign },
    { label: "Winners", value: "15,000+", icon: Award }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Please upload a PDF or image file');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
      toast.success('File uploaded successfully');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.brandName || !formData.contactPerson || !formData.email || !formData.phone || !formData.partnershipType) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('brandName', formData.brandName);
      formDataToSend.append('contactPerson', formData.contactPerson);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('website', formData.website);
      formDataToSend.append('partnershipType', formData.partnershipType);
      formDataToSend.append('productCategory', formData.productCategory);
      formDataToSend.append('message', formData.message);
      if (file) {
        formDataToSend.append('file', file);
      }

      const response = await fetch(`${window.location.origin}/api/partnership/submit`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Partnership inquiry submitted successfully!');
        setFormData({
          brandName: '',
          contactPerson: '',
          email: '',
          phone: '',
          website: '',
          partnershipType: '',
          productCategory: '',
          message: ''
        });
        setFile(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit inquiry');
      }
    } catch (error) {
      console.error('Error submitting partnership form:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('partnership-form').scrollIntoView({ behavior: 'smooth' });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black pt-24 pb-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="animate-fade-in-up">
            <CheckCircle className="w-24 h-24 text-yellow-400 mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-white mb-4">Thank you for your interest!</h1>
            <p className="text-xl text-gray-300 mb-8">
              Our partnerships team will reach out shortly to discuss opportunities.
            </p>
            <div className="flex gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/'}
                className="gaming-button"
              >
                Back to Home
              </Button>
              <Button 
                onClick={() => setSubmitted(false)}
                className="gaming-button-secondary"
              >
                Submit Another Inquiry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 mb-6 px-4 py-2 text-lg">
            Brand Partnerships
          </Badge>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in-up">
            Partner With
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-blue-400"> FokatMain</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            Turn your products into prizes players love. Reach engaged Gen-Z audiences through skill-based gaming. 
            Your brand becomes the reward players talk about and share.
          </p>
          <Button 
            onClick={scrollToForm}
            className="gaming-button text-lg px-8 py-6 animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            Partner With Us
          </Button>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4 bg-gradient-to-r from-yellow-900/20 to-blue-900/20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <stat.icon className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Why Partner With <span className="text-yellow-400">Us</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyPartnerReasons.map((reason, index) => (
              <Card 
                key={index}
                className="gaming-card hover:scale-105 transition-transform duration-300 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-blue-400 rounded-lg flex items-center justify-center mb-4">
                    <reason.icon className="w-8 h-8 text-black" />
                  </div>
                  <CardTitle className="text-xl text-white">{reason.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400">{reason.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Opportunities */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-900/10 to-purple-900/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Partnership <span className="text-yellow-400">Opportunities</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnershipOpportunities.map((opp, index) => (
              <Card 
                key={index}
                className="gaming-card group hover:scale-105 transition-transform duration-300 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-400 rounded-lg flex items-center justify-center mb-4">
                    <opp.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl text-white group-hover:text-yellow-400 transition-colors">
                    {opp.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400">{opp.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Form Section */}
      <section id="partnership-form" className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <Card className="gaming-card">
            <CardHeader>
              <div className="text-center mb-6">
                <Building className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <CardTitle className="text-3xl text-white mb-2">Partner With Us</CardTitle>
                <p className="text-gray-400">Share your details and our partnerships team will contact you shortly</p>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Brand Name */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Brand / Company Name <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      type="text"
                      name="brandName"
                      value={formData.brandName}
                      onChange={handleInputChange}
                      placeholder="Enter your brand or company name"
                      className="bg-black/30 border-gray-700 text-white pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Contact Person */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Contact Person Name <span className="text-red-400">*</span>
                  </label>
                  <Input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    className="bg-black/30 border-gray-700 text-white"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@company.com"
                      className="bg-black/30 border-gray-700 text-white pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Phone / WhatsApp Number <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 XXXXX XXXXX"
                      className="bg-black/30 border-gray-700 text-white pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Website */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Website / Social Link <span className="text-gray-500">(Optional)</span>
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://yourbrand.com"
                      className="bg-black/30 border-gray-700 text-white pl-10"
                    />
                  </div>
                </div>

                {/* Partnership Type */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Type of Partnership <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="partnershipType"
                    value={formData.partnershipType}
                    onChange={handleInputChange}
                    className="w-full bg-black/30 border border-gray-700 text-white rounded-lg px-4 py-3"
                    required
                  >
                    <option value="">Select partnership type</option>
                    <option value="Prize Sponsorship">Prize Sponsorship</option>
                    <option value="Branded Quiz">Branded Quiz</option>
                    <option value="Product Launch">Product Launch</option>
                    <option value="Affiliate/Performance">Affiliate/Performance</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Product Category */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Product Category <span className="text-gray-500">(Optional)</span>
                  </label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      type="text"
                      name="productCategory"
                      value={formData.productCategory}
                      onChange={handleInputChange}
                      placeholder="e.g., Electronics, Fashion, Food & Beverage"
                      className="bg-black/30 border-gray-700 text-white pl-10"
                    />
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Upload Brand Deck / Product Sample <span className="text-gray-500">(Optional)</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-yellow-400 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="brand-file-upload"
                    />
                    <label htmlFor="brand-file-upload" className="cursor-pointer">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      {file ? (
                        <p className="text-yellow-400 font-semibold">{file.name}</p>
                      ) : (
                        <>
                          <p className="text-gray-300 mb-1">Click to upload or drag and drop</p>
                          <p className="text-sm text-gray-500">PDF or Image, max 10MB</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    What do you want to promote? <span className="text-gray-500">(Optional)</span>
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your brand, products, and partnership goals..."
                    className="bg-black/30 border-gray-700 text-white min-h-32"
                    rows={4}
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="gaming-button w-full text-lg py-6"
                >
                  {loading ? 'Submitting...' : 'Submit Partnership Inquiry'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Partnership;
